package no.vareflow.web

import no.vareflow.app.domain.*

/**
 * Svinn (lost stock: broken, expired, spilled, staff …). Registered from a product in Telling
 * (details → "Registrer svinn") or here; listed per month in Mer → Svinn, summed in Analyse and
 * in the report. Anyone who counts may register; admins change and delete.
 */

/** Registrer / rediger svinn for one product. */
fun WebApp.svinnDialog(product: Product?, user: UserAccount, existing: SvinnRecord? = null) {
    val canSeePrice = AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)
    val state = object {
        var chosen: Product? = product
        var search = ""
        var quantity = existing?.let { formatQuantity(it.quantity) }.orEmpty()
        var reason: SvinnReason? = existing?.reason
        var note = existing?.note.orEmpty()
        var error: String? = null
    }
    val title = if (existing != null) t("Rediger svinn", "Edit svinn") else t("Registrer svinn", "Register svinn")
    openDialog(title) { close ->
        val p = state.chosen
        if (existing == null && p == null) {
            // From Mer → Svinn: find the product first.
            input(state.search, "svinn-search", "input", t("Søk etter produkt eller varenummer", "Search for product or item number"), "search") {
                state.search = it; requestRender()
            }.attr("aria-label", t("Søk etter produkt", "Search for product"))
            val q = state.search.trim().lowercase()
            if (q.length >= 2) {
                val hits = core.products.filter { it.active && (it.name.lowercase().contains(q) || it.varenummer.lowercase().contains(q)) }
                    .sortedWith { a, b -> compareNorwegian(a.name, b.name) }.take(30)
                div("picker-list") {
                    if (hits.isEmpty()) div("muted small", t("Ingen treff.", "No matches."))
                    hits.forEach { hit ->
                        el("button", "picker-item") {
                            attr("type", "button")
                            div("strong", hit.name)
                            div("muted tiny", "${hit.category} · ${hit.unit}" + if (hit.varenummer.isNotBlank()) " · ${hit.varenummer}" else "")
                            onClick { state.chosen = hit; requestRender() }
                        }
                    }
                }
            } else p(t("Skriv minst to bokstaver.", "Type at least two letters."), "muted tiny")
            dialogActions { button(t("Avbryt", "Cancel"), "btn text") { close() } }
            return@openDialog
        }
        val name = existing?.productName ?: p!!.name
        val unit = existing?.unit ?: p!!.unit
        val price = existing?.pricePerUnit ?: p!!.pricePerUnit
        div("strong", name)
        div("muted small", (existing?.category ?: p!!.category) + " · " + unit)
        field(t("Antall ($unit)", "Quantity ($unit)"), state.quantity, "svinn-qty", placeholder = "0") { state.quantity = it; requestRender() }
        div("field") {
            span("field-label", t("Årsak", "Reason"))
            div("chip-row wrap") {
                SvinnReason.entries.forEach { r -> chip(r.label, state.reason == r) { state.reason = r; state.error = null; requestRender() } }
            }
        }
        field(t("Kommentar (valgfritt)", "Comment (optional)"), state.note, "svinn-note", placeholder = t("f.eks. mistet på lageret", "e.g. dropped in the store room")) { state.note = it }
        val qty = parseQuantityInput(state.quantity)
        if (canSeePrice && qty != null && qty > 0) div("muted small", t("Verdi", "Value") + ": ${formatPrice(qty * price)} kr")
        errorText(state.error)
        dialogActions {
            if (existing != null && user.role == Role.ADMIN) button(t("Slett", "Delete"), "btn text danger-text") {
                close()
                if (core.deleteSvinn(existing.id, user)) toast(t("Svinnet er slettet.", "The svinn has been deleted."))
            }
            span("spacer")
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Lagre", "Save"), "btn primary") {
                val amount = parseQuantityInput(state.quantity)
                val reason = state.reason
                state.error = when {
                    amount == null || amount <= 0.0 -> t("Skriv inn et antall over 0.", "Enter a quantity above 0.")
                    reason == null -> t("Velg en årsak.", "Choose a reason.")
                    else -> null
                }
                if (state.error != null) { requestRender(); return@button }
                val ok = if (existing != null) core.updateSvinn(existing.id, amount!!, reason!!, state.note, user)
                    else core.addSvinn(p!!, amount!!, reason!!, state.note, user) != null
                if (ok) {
                    close()
                    toast(t("Svinn registrert: ${formatQuantity(amount)} $unit $name.", "Svinn registered: ${formatQuantity(amount)} $unit $name."))
                }
            }
        }
    }
}

/** "2,5" and "2.5" both work. */
private fun parseQuantityInput(text: String): Double? = text.trim().replace(',', '.').toDoubleOrNull()

/** Mer → Svinn: the month's registrations with sums per årsak and hovedvare. */
fun WebApp.renderSvinn(root: El, user: UserAccount) {
    val canSeePrice = AccessRules.hasPermission(user, Permission.PRODUCT_VIEW_PRICE)
    val current = shownOpenSession()?.yearMonth ?: currentYearMonth()
    val months = (core.svinn.map { it.yearMonth } + current).distinct().sortedDescending()
    if (svinnMonth !in months) svinnMonth = current
    val month = svinnMonth ?: current
    root.button(t("‹ Mer", "‹ More"), "btn text back") { moreView = "main"; requestRender() }
    root.pageHeader(t("Svinn", "Svinn"), t("Knust, utgått, søl, personal …", "Broken, expired, spilled, staff …")) {
        button(t("+ Registrer svinn", "+ Register svinn"), "btn primary") { svinnDialog(null, user) }
    }
    if (months.size > 1) root.div("chip-row wrap") {
        months.forEach { m -> chip(formatYearMonth(m), m == month) { svinnMonth = m; requestRender() } }
    }
    val records = core.svinnFor(month)
    if (records.isEmpty()) {
        root.emptyCard(t("Ingen svinn registrert i ${formatYearMonth(month)}. Registrer fra Telling (trykk på en vare) eller med knappen over.",
            "No svinn registered in ${formatYearMonth(month)}. Register from Telling (tap a product) or with the button above."))
        return
    }
    val summary = summarizeSvinn(records, core.orderedCategories())
    root.div("card") {
        div("row") {
            div("grow") {
                div("card-title", formatYearMonth(month))
                div("muted small", if (summary.count == 1) t("1 registrering", "1 registration") else t("${summary.count} registreringer", "${summary.count} registrations"))
            }
            if (canSeePrice) div("metric-value", "${formatPrice(summary.value)} kr")
        }
        if (canSeePrice) {
            div("svinn-split") {
                div {
                    div("field-label", t("Per årsak", "Per reason"))
                    summary.byReason.forEach { (r, v) -> detailRow(r.label, "${formatPrice(v)} kr") }
                }
                div {
                    div("field-label", t("Per hovedvare", "Per main category"))
                    summary.byCategory.forEach { (c, v) -> detailRow(c, "${formatPrice(v)} kr") }
                }
            }
        }
    }
    root.div("list") {
        records.forEach { r ->
            el("button", "card product-row svinn-row") {
                attr("type", "button")
                attr("data-key", "svinn-${r.id}")
                div("grow") {
                    div("strong", r.productName)
                    div("muted small", "${formatQuantity(r.quantity)} ${r.unit} · ${r.reason.label} · ${r.category}")
                    div("muted tiny", "${r.userName} · ${formatDateTime(r.createdAt)}" + if (r.note.isNotBlank()) " · «${r.note}»" else "")
                }
                if (canSeePrice) span("strong", "${formatPrice(r.value)} kr")
                onClick {
                    if (user.role == Role.ADMIN) svinnDialog(null, user, r)
                }
            }
        }
    }
}
